import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCalendarCheck } from "@fortawesome/pro-regular-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../../store/modal/modalReducer";
import axios from "axios";
import { toast } from "react-toastify";
import ErrorBoundary from "../../containers/ErrorBoundary";
import NotificationToast from "../Helpers/NotificationToast";
import CustomButton from "../Helpers/CustomButton";
import Select from "react-select";

// Modal for generating dynamic appointment links
export default function ModalAppointmentLink(props) {
    const dispatch = useDispatch();
    const [open] = useState(true);
    const { current_booking } = useSelector((state) => state.booking);
    
    const [properties, setProperties] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [filteredAppointments, setFilteredAppointments] = useState([]);
    const [templates, setTemplates] = useState([]);
    
    const [selectedProperty, setSelectedProperty] = useState("");
    const [selectedAppointment, setSelectedAppointment] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [loading, setLoading] = useState(true);

    // Get props
    const { setFieldValue, value, platform } = props;

    // Get edit link
    let edit_link = "";
    if (current_booking?.edit_link !== null) {
        edit_link = current_booking?.edit_link?.edit_url;
    } else if (current_booking?.user?.edit_url) {
        edit_link = current_booking?.user.edit_url;
    }

    // Fetch properties and appointments on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}appointment_link_data`
                );
                setProperties(response.data.properties);
                setAppointments(response.data.appointments);
                setTemplates(response.data.templates || []);
                
                // Pre-select current booking's property if available
                if (current_booking?.property_id) {
                    setSelectedProperty(current_booking.property_id.toString());
                }
                
                setLoading(false);
            } catch (error) {
                console.error("Error fetching appointment data:", error);
                toast(
                    <NotificationToast
                        title="Error loading data"
                        message="Could not load properties and appointments"
                        type="error"
                    />
                );
                setLoading(false);
            }
        };

        fetchData();
    }, [current_booking?.property_id]);

    // Filter appointments when property changes
    useEffect(() => {
        if (selectedProperty) {
            const filtered = appointments.filter(
                (apt) => apt.property_id === parseInt(selectedProperty)
            );
            setFilteredAppointments(filtered);
            setSelectedAppointment(""); // Reset appointment selection
        } else {
            setFilteredAppointments([]);
            setSelectedAppointment("");
        }
    }, [selectedProperty, appointments]);

    const handleGenerateLink = () => {
        if (!selectedProperty || !selectedAppointment) {
            toast(
                <NotificationToast
                    title="Selection Required"
                    message="Please select both property and appointment type"
                    type="warning"
                />
            );
            return;
        }

        const appointment = appointments.find(
            (apt) => apt.id === parseInt(selectedAppointment)
        );

        if (!appointment) {
            toast(
                <NotificationToast
                    title="Error"
                    message="Selected appointment not found"
                    type="error"
                />
            );
            return;
        }

        // Generate the appointment link
        const appointmentUrl = `${process.env.REACT_APP_LARAVEL_URL}appointment/${appointment.link_url}/${appointment.id}/0/0/${edit_link}`;
        
        // Format link based on platform (email vs whatsapp)
        let formattedLink = appointmentUrl;
        if (platform === "email") {
            const linkText = appointment.link_shortcode || appointment.name;
            formattedLink = `<a href='${appointmentUrl}' target='_blank'>${linkText}</a>`;
        }

        // Get template text if selected
        let templateText = "";
        if (selectedTemplate) {
            const template = templates.find(
                (tmpl) => tmpl.id === selectedTemplate
            );
            if (template) {
                templateText = template.message + "\n\n";
            }
        }

        // Insert template text + link into message field
        const finalMessage = value ? value + " " + templateText + formattedLink : templateText + formattedLink;
        setFieldValue("message", finalMessage);

        // Show success message
        const successMessage = selectedTemplate 
            ? `Template and ${appointment.name} link added to message`
            : `${appointment.name} link added to message`;
        toast(
            <NotificationToast
                title="Link Generated"
                message={successMessage}
                type="success"
            />
        );

        // Close modal
        dispatch(closeModal());
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className="fixed z-50 inset-0 overflow-y-auto"
                onClose={() => dispatch(closeModal())}
            >
                <ErrorBoundary>
                    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </Transition.Child>

                        <span
                            className="hidden sm:inline-block sm:align-middle sm:h-screen"
                            aria-hidden="true"
                        >
                            &#8203;
                        </span>

                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
                                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                                    <button
                                        type="button"
                                        className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                                        onClick={() => dispatch(closeModal())}
                                    >
                                        <span className="sr-only">Close</span>
                                        <FontAwesomeIcon
                                            className="h-6 w-6"
                                            aria-hidden="true"
                                            icon={faTimes}
                                        />
                                    </button>
                                </div>

                                <div>
                                    {/* Header with icon and title */}
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary bg-opacity-10">
                                            <FontAwesomeIcon
                                                className="h-5 w-5 text-primary"
                                                aria-hidden="true"
                                                icon={faCalendarCheck}
                                            />
                                        </div>
                                        <Dialog.Title
                                            as="h3"
                                            className="text-lg font-medium text-gray-900"
                                        >
                                            Generate Appointment Link
                                        </Dialog.Title>
                                    </div>

                                    {/* Form fields */}
                                    <div>
                                        <div>
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                                    <p className="mt-2 text-sm text-gray-500">Loading...</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-4">
                                                    {/* Property Dropdown */}
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Select Property
                                                        </label>
                                                        <select
                                                            value={selectedProperty}
                                                            onChange={(e) => setSelectedProperty(e.target.value)}
                                                            className="w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-primary focus:ring-primary focus:border-primary sm:text-sm"
                                                        >
                                                            <option value="">-- Choose a property --</option>
                                                            {properties.map((property) => (
                                                                <option key={property.value} value={property.value}>
                                                                    {property.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {/* Appointment Type Dropdown */}
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Select Appointment Type
                                                        </label>
                                                        <select
                                                            value={selectedAppointment}
                                                            onChange={(e) => setSelectedAppointment(e.target.value)}
                                                            disabled={!selectedProperty}
                                                            className="w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-primary focus:ring-primary focus:border-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                        >
                                                            <option value="">
                                                                {selectedProperty
                                                                    ? "-- Choose appointment type --"
                                                                    : "-- Select a property first --"}
                                                            </option>
                                                            {filteredAppointments.map((appointment) => (
                                                                <option key={appointment.id} value={appointment.id}>
                                                                    {appointment.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {selectedProperty && filteredAppointments.length === 0 && (
                                                            <p className="mt-1 text-sm text-gray-500">
                                                                No appointments available for this property
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Template Dropdown (Optional) */}
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Select Template (Optional)
                                                        </label>
                                                        <Select
                                                            value={templates.find(t => t.id === selectedTemplate) ? {
                                                                value: selectedTemplate,
                                                                label: templates.find(t => t.id === selectedTemplate).name
                                                            } : null}
                                                            onChange={(option) => setSelectedTemplate(option ? option.value : "")}
                                                            options={[
                                                                { value: "", label: "-- No template --" },
                                                                ...templates.map((template) => ({
                                                                    value: template.id,
                                                                    label: template.name
                                                                }))
                                                            ]}
                                                            isClearable
                                                            placeholder="Search templates..."
                                                            className="text-sm"
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                            styles={{
                                                                control: (base) => ({
                                                                    ...base,
                                                                    borderColor: '#d1d5db',
                                                                    '&:hover': { borderColor: '#d1d5db' },
                                                                }),
                                                                option: (base, state) => ({
                                                                    ...base,
                                                                    backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#dbeafe' : 'white',
                                                                    color: state.isSelected ? 'white' : '#374151',
                                                                }),
                                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                            }}
                                                        />
                                                    </div>

                                                    {/* Template Preview with Link */}
                                                    {selectedTemplate && selectedAppointment && (
                                                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                                            <p className="text-xs font-medium text-green-700 mb-2">
                                                                Complete Message Preview:
                                                            </p>
                                                            <div className="text-xs text-gray-700 bg-white p-3 rounded border border-gray-200 max-h-48 overflow-y-auto break-all" style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap'}}>
                                                                {(() => {
                                                                    const template = templates.find(
                                                                        (tmpl) => tmpl.id === selectedTemplate
                                                                    );
                                                                    const appointment = appointments.find(
                                                                        (apt) => apt.id === parseInt(selectedAppointment)
                                                                    );
                                                                    if (!template || !appointment) return "";
                                                                    
                                                                    const appointmentUrl = `${process.env.REACT_APP_LARAVEL_URL}appointment/${appointment.link_url}/${appointment.id}/0/0/${edit_link}`;
                                                                    
                                                                    return template.message + "\n\n" + appointmentUrl;
                                                                })()}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Link Preview */}
                                                    {selectedAppointment && !selectedTemplate && (
                                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                                            <p className="text-xs font-medium text-blue-700 mb-2">
                                                                Link Preview:
                                                            </p>
                                                            <p className="text-xs text-gray-600 break-all font-mono bg-white p-2 rounded border border-gray-200">
                                                                {(() => {
                                                                    const appointment = appointments.find(
                                                                        (apt) => apt.id === parseInt(selectedAppointment)
                                                                    );
                                                                    if (!appointment) return "";
                                                                    return `${process.env.REACT_APP_LARAVEL_URL}appointment/${appointment.link_url}/${appointment.id}/0/0/${edit_link}`;
                                                                })()}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                    <button
                                        type="button"
                                        onClick={handleGenerateLink}
                                        disabled={!selectedProperty || !selectedAppointment || loading}
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary_hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Generate Link
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => dispatch(closeModal())}
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </Transition.Child>
                    </div>
                </ErrorBoundary>
            </Dialog>
        </Transition.Root>
    );
}
